import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Connector, ConnectorType, CONNECTOR_TYPE_LABELS, PartnerDocument } from "@/types";
import { toast } from "sonner";
import { Camera, Upload, X, Image as ImageIcon, FileText, RotateCcw } from "lucide-react";

interface AddEditChannelPartnerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner?: Connector | null;
  onSave: (partner: Partial<Connector>) => void;
}

export function AddEditChannelPartnerSheet({ 
  open, 
  onOpenChange, 
  partner, 
  onSave 
}: AddEditChannelPartnerSheetProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<PartnerDocument[]>([]);
  const [form, setForm] = useState<Partial<Connector>>({
    name: "",
    type: "broker",
    whatsappNumber: "",
    secondaryNumber: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    remark: "",
  });

  useEffect(() => {
    if (partner) {
      setForm({
        name: partner.name || "",
        type: partner.type || "broker",
        whatsappNumber: (partner.whatsappNumber || '').replace(/^\+91/, ''),
        secondaryNumber: (partner.secondaryNumber || '').replace(/^\+91/, ''),
        email: partner.email || "",
        address: partner.address || "",
        city: partner.city || "",
        state: partner.state || "",
        pincode: partner.pincode || "",
        remark: partner.remark || "",
      });
      // Load existing documents when editing
      setDocuments(partner.documents || []);
    } else {
      setForm({
        name: "",
        type: "broker",
        whatsappNumber: "",
        secondaryNumber: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        remark: "",
      });
      setDocuments([]);
    }
  }, [partner, open]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newDocs: PartnerDocument[] = Array.from(files).map(file => ({
        id: `doc_${Date.now()}_${file.name}`,
        name: file.name,
        type: file.type,
        size: file.size,
      }));
      setDocuments(prev => [...prev, ...newDocs]);
    }
  };

  const handleRemoveDocument = (id: string) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  };

  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.whatsappNumber?.trim() || !/^[6-9]\d{9}$/.test(form.whatsappNumber.trim())) {
      toast.error("Enter valid 10-digit mobile number starting with 6-9");
      return;
    }
    if (form.secondaryNumber?.trim() && !/^[6-9]\d{9}$/.test(form.secondaryNumber.trim())) {
      toast.error("Enter valid 10-digit secondary number starting with 6-9");
      return;
    }
    // Include documents in the save
    onSave({ ...form, documents });
    onOpenChange(false);
    toast.success(partner ? "Partner updated successfully" : "Partner added successfully");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-center gap-2">
            <SheetTitle className="text-xl font-bold">{partner ? "Edit Partner" : "Add New Partner"}</SheetTitle>
            {!partner && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => { setForm({ name: '', type: 'broker', whatsappNumber: '', secondaryNumber: '', email: '', address: '', city: '', state: '', pincode: '', remark: '' }); setDocuments([]); }}
                className="h-8 w-8 rounded-lg shrink-0"
                title="Clear form"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetHeader>
        
        <div className="space-y-5 pb-8">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Name *</Label>
            <Input 
              value={form.name || ""} 
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Enter partner name"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Type</Label>
            <Select 
              value={form.type} 
              onValueChange={(v) => setForm(p => ({ ...p, type: v as ConnectorType }))}
            >
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CONNECTOR_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">WhatsApp *</Label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+91</span>
                <Input 
                  value={form.whatsappNumber || ""} 
                  onChange={(e) => { const digits = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(p => ({ ...p, whatsappNumber: digits })); }}
                  placeholder="9876543210"
                  type="tel"
                  maxLength={10}
                  className="h-12 rounded-l-none"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Secondary</Label>
              <Input 
                value={form.secondaryNumber || ""} 
                onChange={(e) => { const digits = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(p => ({ ...p, secondaryNumber: digits })); }}
                placeholder="Optional"
                type="tel"
                maxLength={10}
                className="h-12"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Email</Label>
            <Input 
              type="email" 
              value={form.email || ""} 
              onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="email@example.com"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Address</Label>
            <Input 
              value={form.address || ""} 
              onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
              placeholder="Street address"
              className="h-12"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">City</Label>
              <Input 
                value={form.city || ""} 
                onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
                placeholder="City"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Pincode</Label>
              <Input 
                value={form.pincode || ""} 
                onChange={(e) => setForm(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="400001"
                maxLength={6}
                inputMode="numeric"
                className="h-12"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">State</Label>
            <Input 
              value={form.state || ""} 
              onChange={(e) => setForm(p => ({ ...p, state: e.target.value }))}
              placeholder="State"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Remark</Label>
            <Textarea 
              value={form.remark || ""} 
              onChange={(e) => setForm(p => ({ ...p, remark: e.target.value }))}
              placeholder="Any notes about this partner..."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Attachments / Visiting Card Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Documents / Visiting Card</Label>
            
            {/* Document Preview */}
            {documents.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {documents.map(doc => (
                  <Badge key={doc.id} variant="secondary" className="flex items-center gap-1.5 py-1.5 px-2.5">
                    {doc.type.startsWith('image/') ? <Camera className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
                    <span className="max-w-[100px] truncate text-xs">{doc.name}</span>
                    <button 
                      onClick={() => handleRemoveDocument(doc.id)} 
                      type="button"
                      className="ml-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Upload Options */}
            <div className="border-2 border-dashed border-border rounded-xl p-4">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="flex flex-col gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start rounded-xl"
                  onClick={handleCameraCapture}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Photo
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start rounded-xl"
                  onClick={handleFileSelect}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Photos, documents, PDFs, etc.
              </p>
            </div>
          </div>

          <Button 
            onClick={handleSave} 
            className="w-full h-12 mt-4 bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985] text-white font-medium"
          >
            {partner ? "Save Changes" : "Add Partner"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
